import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBookmark,
  FaCreditCard,
  FaGift,
  FaListAlt,
  FaQuestionCircle,
  FaRegBookmark,
  FaSignOutAlt,
  FaTimes,
  FaUser,
} from 'react-icons/fa';

import { useSidebarState } from '../../hooks/useSidebarState';
import LanguageSelector from '../languageSelector/LanguageSelector';
import { fetchTokenBalanceAsync, redeemCodeAsync } from '../auth/tokenApi';
import { useAuth } from '../auth/AuthContext';
import keycloak from '../auth/keycloak';
import TokenPurchaseModal from '../tokenPurchase/TokenPurchaseModal';
import HelpModal from '../help/HelpModal';
import en from '../comicForm/translations/en';
import { asIcon } from '../icons';
import './AppShell.css';

const MenuUserIcon = asIcon(FaUser);
const MenuCardIcon = asIcon(FaCreditCard);
const MenuGiftIcon = asIcon(FaGift);
const MenuTransactionsIcon = asIcon(FaListAlt);
const MenuHelpIcon = asIcon(FaQuestionCircle);
const MenuLogoutIcon = asIcon(FaSignOutAlt);
const RedeemCloseIcon = asIcon(FaTimes);
const SidebarPinnedIcon = asIcon(FaBookmark);
const SidebarUnpinnedIcon = asIcon(FaRegBookmark);

export type AppShellPage = 'comic' | 'transactions';
export type AppShellTranslations = typeof en;

interface AppShellProps {
  children: ReactNode;
  page: AppShellPage;
  translations: AppShellTranslations;
  language: string;
  changeLanguage: (language: string) => void;
  className?: string;
}

interface AppShellContextValue {
  credits: number | null;
  isBalanceLoading: boolean;
  refreshTokenBalance: () => Promise<void>;
}

const AppShellContext = React.createContext<AppShellContextValue | null>(null);

export const useAppShell = (): AppShellContextValue => {
  const context = useContext(AppShellContext);

  if (!context) {
    throw new Error('useAppShell must be used inside AppShell');
  }

  return context;
};

const AppShell: React.FC<AppShellProps> = ({
  children,
  page,
  translations,
  language,
  changeLanguage,
  className = '',
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { isSidebarExpanded, isPinned, sidebarHoverHandlers, togglePinned } =
    useSidebarState();
  const [credits, setCredits] = useState<number | null>(null);
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isPurchaseOpen, setIsPurchaseOpen] = useState(false);
  const [isRedeemOpen, setIsRedeemOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemFeedback, setRedeemFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const profileName =
    keycloak.tokenParsed?.name ||
    [keycloak.tokenParsed?.given_name, keycloak.tokenParsed?.family_name]
      .filter(Boolean)
      .join(' ') ||
    keycloak.tokenParsed?.preferred_username ||
    keycloak.tokenParsed?.email ||
    '';
  const profileInitial = (profileName || 'U').charAt(0).toUpperCase();

  const loadTokenBalance = useCallback(async () => {
    setIsBalanceLoading(true);

    try {
      setCredits(await fetchTokenBalanceAsync());
    } catch (error) {
      console.error('Error fetching token balance:', error);
    } finally {
      setIsBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadTokenBalance();
    }
  }, [isAuthenticated, loadTokenBalance]);

  useEffect(() => {
    if (!isProfileOpen) return;

    const closeProfileOnOutsideClick = (event: MouseEvent | TouchEvent) => {
      const target = event.target;

      if (target instanceof Node && profileMenuRef.current?.contains(target)) {
        return;
      }

      setIsProfileOpen(false);
    };

    document.addEventListener('mousedown', closeProfileOnOutsideClick);
    document.addEventListener('touchstart', closeProfileOnOutsideClick);

    return () => {
      document.removeEventListener('mousedown', closeProfileOnOutsideClick);
      document.removeEventListener('touchstart', closeProfileOnOutsideClick);
    };
  }, [isProfileOpen]);

  const openKeycloakAccount = () => {
    const accountUrl = `${keycloak.authServerUrl}/realms/${keycloak.realm}/account/`;
    window.open(accountUrl, '_blank', 'noopener,noreferrer');
  };

  const handleRedeemSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const code = redeemCode.trim();
    if (!code || isRedeeming) return;

    setIsRedeeming(true);
    setRedeemFeedback(null);

    try {
      await redeemCodeAsync(code);
      await loadTokenBalance();
      setRedeemCode('');
      setIsRedeemOpen(false);
      setRedeemFeedback({
        type: 'success',
        message: translations.codeRedeemed,
      });
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response
        ?.status;
      const message =
        status === 400
          ? translations.codeAlreadyUsed
          : status === 404
            ? translations.codeNotFound
            : translations.failedToRedeem;

      setRedeemFeedback({ type: 'error', message });
    } finally {
      setIsRedeeming(false);
    }
  };

  const closeRedeem = () => {
    setIsRedeemOpen(false);
    setRedeemCode('');
  };

  const openHelp = () => {
    setIsProfileOpen(false);
    setIsHelpOpen(true);
  };

  const shellClassName = [
    'app-shell',
    className,
    isSidebarExpanded ? '' : 'sidebar-is-collapsed',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <AppShellContext.Provider
      value={{
        credits,
        isBalanceLoading,
        refreshTokenBalance: loadTokenBalance,
      }}
    >
      <div className={shellClassName}>
        <div className="app-toolbar">
          <div
            className={`sidebar-shell ${isSidebarExpanded ? '' : 'sidebar-is-collapsed'}`}
            {...sidebarHoverHandlers}
          >
            <div className="sidebar-brand">
              <div className="sidebar-brand-button">
                <img
                  className="sidebar-brand-full sidebar-brand-logo-full"
                  src="/scicomic-logo-sidebar.png"
                  alt="Scicomic"
                />
                <img
                  className="sidebar-brand-short sidebar-brand-logo-short"
                  src="/scicomic-logo-abbr.png"
                  alt="Scicomic"
                />
              </div>

              <button
                type="button"
                className={`sidebar-pin-button ${isPinned ? 'active' : ''}`}
                onClick={togglePinned}
                aria-label={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
                title={isPinned ? 'Unpin sidebar' : 'Pin sidebar'}
              >
                {isPinned ? (
                  <SidebarPinnedIcon size={17} />
                ) : (
                  <SidebarUnpinnedIcon size={17} />
                )}
              </button>
            </div>

            <div className="user-profile" ref={profileMenuRef}>
              {isProfileOpen && (
                <div className="profile-details">
                  <button
                    type="button"
                    className="profile-menu-item"
                    onClick={openKeycloakAccount}
                  >
                    <MenuUserIcon size={14} />
                    <span>{translations.editProfile}</span>
                  </button>
                  <button
                    type="button"
                    className="profile-menu-item"
                    onClick={() => {
                      setIsPurchaseOpen(true);
                      setIsProfileOpen(false);
                    }}
                  >
                    <MenuCardIcon size={14} />
                    <span>{translations.purchaseTokens}</span>
                  </button>
                  <button
                    type="button"
                    className="profile-menu-item"
                    onClick={() => {
                      setIsProfileOpen(false);
                      setIsRedeemOpen(true);
                      setRedeemFeedback(null);
                    }}
                  >
                    <MenuGiftIcon size={14} />
                    <span>{translations.redeemCode}</span>
                  </button>
                  <button
                    type="button"
                    className={`profile-menu-item ${page === 'transactions' ? 'active' : ''}`}
                    onClick={() => {
                      setIsProfileOpen(false);
                      navigate('/transactions');
                    }}
                  >
                    <MenuTransactionsIcon size={14} />
                    <span>{translations.transactions}</span>
                  </button>
                  <button
                    type="button"
                    className="profile-menu-item"
                    onClick={openHelp}
                  >
                    <MenuHelpIcon size={14} />
                    <span>{translations.help}</span>
                  </button>
                  <div className="profile-menu-divider" />
                  <button
                    type="button"
                    className="profile-menu-item profile-menu-item-danger"
                    onClick={logout}
                  >
                    <MenuLogoutIcon size={14} />
                    <span>{translations.logout}</span>
                  </button>
                </div>
              )}

              <button
                type="button"
                className="profile-summary"
                onClick={() => setIsProfileOpen((open) => !open)}
              >
                <div
                  className={`user-icon-wrapper ${isBalanceLoading ? 'loading' : ''}`}
                >
                  <span className="profile-initial">{profileInitial}</span>
                  {isBalanceLoading && <span className="icon-spinner" />}
                </div>
                <div className="profile-summary-text">
                  <span className="profile-name">{profileName}</span>
                  <span className="profile-credits">
                    <span>{credits ?? '...'}</span>
                    <img
                      src="/token-icon.png"
                      alt="Tokens"
                      className="profile-token-icon"
                    />
                  </span>
                </div>
              </button>
            </div>
          </div>

          <LanguageSelector
            language={language}
            changeLanguage={changeLanguage}
          />
        </div>

        {children}

        <TokenPurchaseModal
          isOpen={isPurchaseOpen}
          onClose={() => setIsPurchaseOpen(false)}
          labels={{
            title: translations.selectTokenPackage,
            individual: translations.individual,
            professional: translations.professional,
            close: translations.closePurchaseModal,
            configurationError: translations.stripeConfigurationError,
          }}
        />

        {isRedeemOpen && (
          <div className="redeem-modal-backdrop" onClick={closeRedeem}>
            <div
              className="redeem-modal"
              onClick={(event) => event.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="redeem-modal-header">
                <h2>{translations.redeemCode}</h2>
                <button
                  type="button"
                  className="redeem-modal-close"
                  onClick={closeRedeem}
                >
                  <RedeemCloseIcon size={22} />
                </button>
              </div>
              <form className="redeem-modal-form" onSubmit={handleRedeemSubmit}>
                <input
                  className="redeem-modal-input"
                  type="text"
                  value={redeemCode}
                  onChange={(event) => setRedeemCode(event.target.value)}
                  placeholder={translations.enterCode}
                  autoFocus
                />
                {redeemFeedback && (
                  <p className={`redeem-feedback ${redeemFeedback.type}`}>
                    {redeemFeedback.message}
                  </p>
                )}
                <div className="redeem-modal-actions">
                  <button
                    type="button"
                    className="redeem-modal-cancel"
                    onClick={closeRedeem}
                  >
                    {translations.cancel}
                  </button>
                  <button
                    type="submit"
                    className="redeem-modal-submit"
                    disabled={!redeemCode.trim() || isRedeeming}
                  >
                    {isRedeeming ? translations.redeeming : translations.redeem}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <HelpModal
          isOpen={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          labels={{
            title: translations.help,
            getStartedTitle: translations.helpGetStartedTitle,
            getStartedStep1: translations.helpGetStartedStep1,
            getStartedStep2: translations.helpGetStartedStep2,
            getStartedStep3: translations.helpGetStartedStep3,
            writingTitle: translations.helpWritingTitle,
            writingTip1: translations.helpWritingTip1,
            writingTip2: translations.helpWritingTip2,
            writingTip3: translations.helpWritingTip3,
            generationTitle: translations.helpGenerationTitle,
            generationTip1: translations.helpGenerationTip1,
            generationTip2: translations.helpGenerationTip2,
            generationTip3: translations.helpGenerationTip3,
            contactTitle: translations.helpContactTitle,
            contactDescription: translations.helpContactDescription,
            gotIt: translations.helpGotIt,
            version: translations.helpVersion,
          }}
        />
      </div>
    </AppShellContext.Provider>
  );
};

export default AppShell;
